import { useMemo } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import BackToTop from "@/components/feature/BackToTop";
import { useSeo } from "@/hooks/useSeo";
import { site } from "@/mocks/site";
import ContactForm from "./components/ContactForm";

const channels = [
  {
    icon: "ri-file-upload-line",
    title: "提供线索",
    desc: "手上有截图、录音、单据等原始材料，希望我们立档求证。",
    value: site.tipEmail,
  },
  {
    icon: "ri-flag-line",
    title: "报告纠错",
    desc: "认为某条档案的结论、来源或表述存在问题，欢迎指出。",
    value: site.email,
  },
  {
    icon: "ri-team-line",
    title: "内容合作",
    desc: "媒体转载、内容共创或方法论交流等相关合作。",
    value: site.email,
  },
];

const checklist = [
  "事件发生的大致时间与地点",
  "流传说法的原始出处或链接",
  "你手上材料的形式（截图 / 录音 / 文件 / 现场拍摄）",
  "材料是否为你本人取得，能否提供更完整的原始件",
];

export default function ContactPage() {
  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系我们｜提交线索与纠错 · 拾真社",
      url: "https://hdlyjgs.cn/contact",
      inLanguage: "zh-CN",
      description: "向拾真社提交独家爆料线索、报告档案纠错或进行内容合作。我们接受匿名投稿并对来源信息脱敏处理。",
      mainEntity: {
        "@type": "Organization",
        name: "拾真社",
        email: site.email,
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "线索投稿与纠错",
          email: site.tipEmail,
          availableLanguage: "zh-CN",
        },
      },
    }),
    [],
  );

  useSeo({
    title: "联系我们｜提交独家爆料线索与档案纠错 · 拾真社",
    description:
      "有独家爆料线索、原始材料或档案纠错意见？可通过拾真社联系页面提交。我们接受匿名投稿，对来源信息脱敏处理，材料仅用于求证。",
    keywords: "独家爆料投稿,提供线索,档案纠错,联系拾真社",
    path: "/contact",
    jsonLd,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader activePath="/contact" />
      <Breadcrumbs items={[{ label: "联系我们" }]} />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1200px] px-4 py-12 md:px-8 md:py-14">
          <p className="flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.2em] text-primary-600">
            <span className="inline-block h-px w-6 bg-primary-400" aria-hidden="true"></span>
            联系我们
          </p>
          <h1 className="mt-5 max-w-3xl font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
            你手上的材料，可能就是缺口
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
            无论是提供线索、指出档案中的错误，还是谈合作，都可以通过下面的表单或邮箱联系我们。
            投稿可以匿名，我们会对来源信息做脱敏处理。
          </p>
        </div>
      </section>

      <main className="mx-auto max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[360px_1fr] lg:gap-12">
          <aside className="space-y-4">
            {channels.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${item.icon} text-[20px]`} aria-hidden="true"></i>
                  </span>
                  <div className="min-w-0">
                    <h2 className="font-heading text-base font-bold text-foreground-950">{item.title}</h2>
                    <p className="mt-1.5 text-[13px] leading-relaxed text-foreground-600">{item.desc}</p>
                    <p className="mt-2 font-label text-[12px] text-primary-600 break-all">{item.value}</p>
                  </div>
                </div>
              </div>
            ))}

            <div className="rounded-lg border border-background-200 bg-foreground-950 p-5">
              <h2 className="font-heading text-base font-bold text-background-50">工作时段</h2>
              <p className="mt-2 text-[13px] leading-relaxed text-background-200">
                周一至周五 10:00–19:00。非工作时段提交的线索我们也会记录，统一在下一个工作日处理。
              </p>
              <p className="mt-4 border-t border-background-50/15 pt-4 text-[13px] leading-relaxed text-background-300">
                涉及个人隐私、未成年人或正在司法程序中的内容，请先说明情况，我们会单独评估处理方式。
              </p>
            </div>
          </aside>

          <div>
            <ContactForm />

            <section aria-labelledby="checklist-heading" className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 id="checklist-heading" className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-list-check-2 text-primary-500" aria-hidden="true"></i>
                提交前，建议先准备好这些
              </h2>
              <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {checklist.map((text) => (
                  <li key={text} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-foreground-700">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-accent-600">
                      <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                    </span>
                    {text}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}